import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import {
  createDefaultQuestion,
  findSurveyOptionProblem,
  type SurveyQuestion,
  type SurveyQuestionType,
  type SurveyStatus,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { AddSurveyQuestionSelect } from './AddSurveyQuestionSelect';
import { useDistrict } from '../districts-admin';
import { SurveyQuestionEditor } from './SurveyQuestionEditor';
import { updateSurvey, useSurveys } from './useSurveys';

/**
 * アンケート編集画面 (admin 第 22 弾、Phase 5 A-S14)。
 *
 * 旧 `old/apps/admin/src/pages/SurveyEditPage.tsx` の visual editor は規模が大きい (10 種類の質問
 * 詳細 widget) ため、本 sprint では minimum scope:
 *   - 基本情報 (title / description / status / collectParticipantProfile / allowEdit) は form 編集
 *   - questions は カード一覧 + add (type 選択) + delete + 上下並び替え のみ
 *   - 各質問の詳細 (options / min / max 等) は JSON textarea fallback (旧 visual editor は別 sprint)
 *
 * 「保存」で 1 回まとめて updateSurvey、戻る link で一覧に。
 *
 * 認可: master / district admin (担当地区) のみ操作可。
 */

const STATUS_LABEL: Record<SurveyStatus, string> = {
  draft: '下書き',
  published: '配信中',
  closed: '締切',
};

function genQuestionId(): string {
  return `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function SurveyEditPage() {
  const { districtId, surveyId } = useParams<{ districtId: string; surveyId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const surveysState = useSurveys(districtId ?? null);
  const { toast } = useToast();

  const target = useMemo(() => {
    if (surveysState.status !== 'ready' || !surveyId) return null;
    return surveysState.items.find((s) => s.id === surveyId) ?? null;
  }, [surveysState, surveyId]);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<SurveyStatus>('draft');
  const [collectProfile, setCollectProfile] = useState(true);
  const [allowEdit, setAllowEdit] = useState(true);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // target が ready になったら form state を初期化 (1 回のみ、以降は user 編集を上書きしない)。
  useEffect(() => {
    if (!target || initialized) return;
    setTitle(target.title);
    setDescription(target.description);
    setStatus(target.status);
    setCollectProfile(target.collectParticipantProfile);
    setAllowEdit(target.allowEdit);
    setQuestions(target.questions);
    setInitialized(true);
  }, [target, initialized]);

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canManage = isMaster || isDistrictAdminOfThis;
  const uid = authState.user?.uid ?? '';

  function handleAddQuestion(type: SurveyQuestionType) {
    const q = createDefaultQuestion(type, genQuestionId());
    setQuestions((prev) => [...prev, q]);
  }

  function handleDeleteQuestion(qid: string) {
    if (!confirm('この質問を削除します。よろしいですか?')) return;
    setQuestions((prev) => prev.filter((q) => q.id !== qid));
  }

  function handleQuestionChange(qid: string, next: SurveyQuestion) {
    setQuestions((prev) => prev.map((q) => (q.id === qid ? next : q)));
  }

  function handleMove(qid: string, dir: -1 | 1) {
    setQuestions((prev) => {
      const idx = prev.findIndex((q) => q.id === qid);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      const a = next[idx];
      const b = next[swap];
      if (!a || !b) return prev;
      next[idx] = b;
      next[swap] = a;
      return next;
    });
  }

  async function handleSave() {
    if (!districtId || !surveyId || !target || !canManage) return;
    const optionProblem = findSurveyOptionProblem(questions);
    if (optionProblem) {
      toast('error', optionProblem.message);
      return;
    }
    setSubmitting(true);
    try {
      await updateSurvey(
        districtId,
        surveyId,
        {
          title: title.trim(),
          description,
          status,
          collectParticipantProfile: collectProfile,
          allowEdit,
          questions,
        },
        uid,
        target.version,
      );
      toast('success', 'アンケートを保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <header className="mb-6">
        <Link
          to={districtId ? `/districts/${districtId}/surveys` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← アンケート一覧
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          アンケート編集
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          基本情報 + 質問一覧。各質問は visual editor で選択肢 / 目盛 / 星数 / 数値範囲などを編集
          (生 JSON は各質問の「上級者向け」から直接編集可)。
        </p>
      </header>

      {!canManage && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区のアンケート管理権限がありません (role: {authState.role ?? 'none'})。
          </p>
        </Card>
      )}

      {surveysState.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-600">読込中…</p>
        </Card>
      )}

      {surveysState.status === 'error' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">読込エラー: {surveysState.error.message}</p>
        </Card>
      )}

      {surveysState.status === 'ready' && !target && (
        <Card padding="md">
          <p className="text-sm text-slate-600">アンケートが見つかりません (id: {surveyId})。</p>
        </Card>
      )}

      {target && (
        <>
          <Card padding="lg" className="mb-6">
            <h2 className="text-base font-bold text-slate-900 mb-3">基本情報</h2>
            <div className="grid grid-cols-1 gap-3">
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">タイトル *</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  disabled={!canManage || submitting}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm"
                />
              </label>
              <label className="grid grid-cols-1 gap-1">
                <span className="text-xs text-slate-500">説明</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  disabled={!canManage || submitting}
                  className="px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">状態</span>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as SurveyStatus)}
                    disabled={!canManage || submitting}
                    className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  >
                    <option value="draft">{STATUS_LABEL.draft}</option>
                    <option value="published">{STATUS_LABEL.published}</option>
                    <option value="closed">{STATUS_LABEL.closed}</option>
                  </select>
                </label>
                <div className="grid grid-cols-1 gap-1">
                  <span className="text-xs text-slate-500">回答時オプション</span>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={collectProfile}
                      onChange={(e) => setCollectProfile(e.target.checked)}
                      disabled={!canManage || submitting}
                      className="mt-1"
                    />
                    <span>参加者プロファイル snapshot を回答に含める</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={allowEdit}
                      onChange={(e) => setAllowEdit(e.target.checked)}
                      disabled={!canManage || submitting}
                      className="mt-1"
                    />
                    <span>提出後の編集を許可する</span>
                  </label>
                </div>
              </div>
            </div>
          </Card>

          <Card padding="lg" className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h2 className="text-base font-bold text-slate-900">質問 ({questions.length} 問)</h2>
              <AddSurveyQuestionSelect
                onAdd={handleAddQuestion}
                disabled={!canManage || submitting}
                ariaLabel="質問を追加（一覧の上）"
              />
            </div>

            {questions.length === 0 && (
              <p className="text-sm text-slate-500">質問はまだありません。</p>
            )}

            <ul className="grid grid-cols-1 gap-3">
              {questions.map((q, idx) => (
                <li key={q.id}>
                  <SurveyQuestionEditor
                    question={q}
                    index={idx}
                    total={questions.length}
                    disabled={!canManage || submitting}
                    onChange={(next) => handleQuestionChange(q.id, next)}
                    onMove={(dir) => handleMove(q.id, dir)}
                    onRemove={() => handleDeleteQuestion(q.id)}
                  />
                </li>
              ))}
            </ul>

            {questions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-slate-200 flex justify-end">
                <AddSurveyQuestionSelect
                  onAdd={handleAddQuestion}
                  disabled={!canManage || submitting}
                  ariaLabel="質問を追加（一覧の下）"
                />
              </div>
            )}
          </Card>

          <div className="flex justify-end gap-2 mb-6">
            <Link
              to={`/districts/${districtId}/surveys`}
              className="min-h-touch flex items-center px-4 text-sm text-slate-600 hover:text-slate-800"
            >
              キャンセル
            </Link>
            <Button onClick={() => void handleSave()} disabled={!canManage || submitting}>
              {submitting ? '保存中…' : '💾 保存'}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
